import { Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { ImagePreviewType } from 'src/app/shared/enums';

import { IImage } from 'src/app/shared/services/posts/posts.type';
import { UtilsService } from 'src/app/shared/services/utils.service';

import { AddBaseUrlPipe } from '../../../pipe/add-baseUrl/add-base-url.pipe';

type Row = { url: string; index: number; colSpan: number }[];

@Component({
  selector: 'app-post-images-grid',
  templateUrl: './post-images-grid.component.html',
  styleUrls: ['./post-images-grid.component.scss'],
  imports: [AddBaseUrlPipe],
})
export class PostImagesGridComponent implements OnChanges {
  @Input() images: IImage[] = [];

  @Input() rowPattern: number[] | null = null;

  @Output() clicked = new EventEmitter<number>();

  private _utils = inject(UtilsService);

  rows: Row[] = [];

  ngOnChanges(): void {
    const items = this.images.map((image, index) => {
      const preview = this._utils.getImagePreviewPath(image, ImagePreviewType.normal);
      return { url: preview, index };
    });
    this.rows = this.packIntoRows(items, this.rowPattern);
  }

  public onClick(index: number) {
    this.clicked.emit(index);
  }

  private packIntoRows(items: { url: string; index: number }[], pattern: number[] | null): Row[] {
    if (!items.length) return [];

    const countsPerRow = this.buildRowCounts(items.length, pattern);

    const rows: Row[] = [];
    let pos = 0;
    for (const count of countsPerRow) {
      const slice = items.slice(pos, pos + count);
      pos += count;
      rows.push(this.assignColspans(slice));
    }
    return rows;
  }

  private buildRowCounts(total: number, pattern: number[] | null): number[] {
    if (pattern && pattern.length) {
      const res: number[] = [];
      let i = 0,
        left = total;
      while (left > 0) {
        const take = Math.min(pattern[i % pattern.length], left);
        res.push(take);
        left -= take;
        i++;
      }
      return res;
    }

    const res: number[] = [];
    let left = total;
    while (left > 0) {
      let n = Math.min(3, left);

      if (left === 1 && res.length) {
        const prev = res[res.length - 1];
        if (prev >= 3) {
          res[res.length - 1] = prev - 1; // 3→2, 4→3
          n = 2;
        } else {
          n = 1;
        }
      }

      n = Math.max(1, Math.min(6, n));
      res.push(n);
      left -= n;
    }
    return res;
  }

  private assignColspans(slice: { url: string; index: number }[]): Row {
    const n = slice.length;

    const presets: Record<number, number[]> = {
      1: [6], // одно фото на всю ширину
      2: [3, 3], // две равные
      3: [4, 2, 2], // крупная + две поменьше (или [2,2,2] если хотите ровный ряд)

      4: [3, 3, 3, 3].map((v, i) => (i < 2 ? 3 : 3)), // фактически 4×? — ниже равномерно решим
      5: [3, 3, 2, 2, 2], // две средние + три маленьких
      6: [2, 1, 1, 1, 1, 0].filter(Boolean) as any, // ↓ переопределим равномерным делением
    };

    let spans: number[];
    if (presets[n]) {
      const sum = presets[n].reduce((a, b) => a + b, 0);
      if (sum === 6) {
        spans = presets[n];
      } else {
        spans = this.evenlyDistribute(n);
      }
    } else {
      spans = this.evenlyDistribute(n);
    }

    return slice.map((it, i) => ({ url: it.url, index: it.index, colSpan: spans[i] }));
  }

  private evenlyDistribute(n: number): number[] {
    const base = Math.floor(6 / n);
    let remainder = 6 % n;
    const spans: number[] = Array.from({ length: n }, () => base);
    for (let i = 0; i < n && remainder > 0; i++, remainder--) {
      spans[i] += 1;
    }
    return spans;
  }
}
